import classNames from 'classnames';
import * as React from 'react';
import type { LanguageViewModel } from '~/Features/Navigation/LanguageViewModel.csharp';
import type { NavigationSectionViewModel } from '~/Features/Navigation/NavigationSectionViewModel.csharp';
import type { NavigationTranslations } from '~/Features/Navigation/NavigationTranslations.csharp';
import { FormattedText } from '~/Features/Txt/TxtBase';
import { Link } from '~/Partials/Link/Link';
import { LanguagePicker } from '~/Partials/Nav/Components/LanguagePicker/LanguagePicker';
import { LanguagesPicker } from '~/Partials/Nav/Components/LanguagesPicker/LanguagesPicker';
import { MenuSection } from '~/Partials/Nav/Components/MenuSection/MenuSection';
import styles from './MenuContent.module.css';

export interface MenuContentProps {
    sections: NavigationSectionViewModel[];
    translations: NavigationTranslations;
    menuColumnsLayout: MenuColumnsLayout;
    languages: LanguageViewModel[];
    isOpened: boolean;
}

export type MenuColumnsLayout = 'three' | 'four' | 'five';

export const MenuContent: React.FC<React.PropsWithChildren<MenuContentProps>> = props => {
    const { sections, translations, menuColumnsLayout, languages, isOpened } = props;

    const getVisibilityClassName = (moveToHeaderOnDesktop: boolean) => (moveToHeaderOnDesktop ? 'mobileOnly' : null);
    const firstOtherAvailableLanguage = languages.filter(el => el.url !== null)[1];

    return (
        <div className={classNames(styles.Content, isOpened ? styles['Variant--opened'] : styles['Variant--closed'])}>
            <div className={styles.Inner}>
                {firstOtherAvailableLanguage && (
                    <div className={styles.NavLanguagePicker}>
                        <LanguagePicker language={firstOtherAvailableLanguage} variant="inline" />
                    </div>
                )}
                <ul className={classNames(styles.Level1, styles[`Level1--${menuColumnsLayout}ColumnLayout`])}>
                    {sections.map((section, idx) => (
                        <li
                            key={idx}
                            className={classNames(
                                styles.MenuSection,
                                getVisibilityClassName(section.moveToHeaderOnDesktop)
                            )}
                        >
                            <MenuSection
                                heading={section.heading}
                                translations={{
                                    open: FormattedText(translations.openSection, { section: section.heading }),
                                    close: FormattedText(translations.closeSection, { section: section.heading }),
                                }}
                            >
                                {section.items.length > 0 && (
                                    <ul className={styles.Level2}>
                                        {section.items.map((menuItem, index) => (
                                            <li key={menuItem.linkUrl + index}>
                                                <Link
                                                    className={styles.Link}
                                                    href={menuItem.linkUrl}
                                                    label={menuItem.title}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </MenuSection>
                        </li>
                    ))}
                </ul>
                <LanguagesPicker languages={languages} />
            </div>
        </div>
    );
};
